export default class Vector {

    /**
     * Creates a new 3D vector.  
     * `new Vector()`  
     * `new Vector(x,y)`  
     * `new Vector(x,y,z)`  
     */
    constructor() {
        this.v = new Float32Array(3);

        switch (arguments.length) {

            case 0: {
                this.x = 0;
                this.y = 0;
                this.z = 0;
            } break;

            case 2: {
                this.x = arguments[0];
                this.y = arguments[1];
                this.z = 0;
            } break;

            case 3: {
                this.x = arguments[0];
                this.y = arguments[1];
                this.z = arguments[2];
            } break;

            default:
                throw new Error("Unable to create Vector, invalid parameter count.");
        }
    }

    get x() { return this.v[0]; }
    get y() { return this.v[1]; }
    get z() { return this.v[2]; }

    set x(v) { this.v[0] = v; }
    set y(v) { this.v[1] = v; }
    set z(v) { this.v[2] = v; }

    /**
     * Gets the ith value of this vector.  
     */
    get(i) {
        switch (i) {
            case 0: return this.x;
            case 1: return this.y;
            case 2: return this.z;
            default:
                throw new Error("Index out of range.");
        }
    }

    /**
     * Set the values of this vector.  
     * `set(x,y,z)`  
     * `set(v)`  
     */
    set() {
        if (arguments.length == 1) {
            let [v] = arguments;
            if (v instanceof Vector) {
                this.x = v.x;
                this.y = v.y;
                this.z = v.z;
            } else {
                throw new Error("Unable to copy vector values, argument not a vector.");
            }
        } else {
            let [i, v] = arguments;

            switch (i) {
                case 0: this.x = v; break;
                case 1: this.y = v; break;
                case 2: this.z = v; break;
                default:
                    throw new Error("Index out of range.");
            }
        }
    }

    /**
     * Computes the cross-product of this and the given vector.  
     */
    cross(v) {
        return new Vector(
            this.y * v.z - this.z * v.y,
            this.z * v.x - this.x * v.z,
            this.x * v.y - this.y * v.x);
    }

    /**
     * Computes the dot-product of this and the given vector.  
     */
    dot(v) {
        return (this.x * v.x) + (this.y * v.y) + (this.z * v.z);
    }

    /**
     * Computes the length of this vector.  
     */
    length() {
        return Math.sqrt(this.dot(this));
    }

    /**
     * Computes a negated copy of this vector.  
     */
    negate() {
        return new Vector(-this.x, -this.y, -this.z);
    }

    /**
     * Normalizes this vector.
     */
    static normalize(v) {
        let m = v.length();
        v.x /= m;
        v.y /= m;
        v.z /= m;
        return v;
    }

    /**
     * Computes a normalized copy of this vector.
     */
    unit() {
        return Vector.normalize(this.clone());
    }

    /**
     * Computes the addition of this and another vector.
     */
    add(v) {
        return new Vector(this.x + v.x, this.y + v.y, this.z + v.z);
    }

    /**
     * Computes the subtraction of this and another vector.
     */
    sub(v) {
        return new Vector(this.x - v.x, this.y - v.y, this.z - v.z);
    }

    /**
     * Computes the multiplication of this and another vector or scalar.
     */
    mul(v) {
        if (v instanceof Vector) return new Vector(this.x * v.x, this.y * v.y, this.z * v.z);
        else return new Vector(this.x * v, this.y * v, this.z * v);
    }

    /**
     * Computes the division of this and another vector or scalar.
     */
    div(v) {
        if (v instanceof Vector) return new Vector(this.x / v.x, this.y / v.y, this.z / v.z);
        else return new Vector(this.x / v, this.y / v, this.z / v);
    }

    /**
     * Determines if this and the given vector are equivalent.
     */
    equals(v) {
        // TODO: Epsilon comparison?
        return (this.x == v.x)
            && (this.y == v.y)
            && (this.z == v.z);
    }

    /**
     * Produces a clone of this vector.
     */
    clone() {
        return new Vector(this.x, this.y, this.z);
    }

    /**
     * Returns an array with `[x,y,z]`.
     */
    toArray() {
        return [this.x, this.y, this.z];
    }
}